// Import-free so node --test can load it.

/** lms-api config/middleware.js: parser.formidable.maxFileSize = 100 MB; a larger file is refused server side. */
export const MAX_UPLOAD_SIZE = 100 * 1024 * 1024;
export const MAX_UPLOAD_SIZE_LABEL = '100 MB';
export const MAX_IMAGE_UPLOAD_SIZE = MAX_UPLOAD_SIZE;

export type UploadKind = 'image' | 'video' | 'audio' | 'document';
/** How the board shows an uploaded file; 'file' is an office file no board viewer can render. */
export type UploadViewer = 'image' | 'video' | 'audio' | 'pdf' | 'file';

export interface UploadCandidate {
  name: string;
  type: string;
  size: number;
}

export interface UploadKindSpec {
  kind: UploadKind;
  extensions: string[];
  mimes: string[];
  /** Human list for copy, in the order the teacher reads it. */
  formats: string;
}

// video and audio are what the Chrome 58 WebView plays natively; no transcoding happens server side
export const UPLOAD_KINDS: UploadKindSpec[] = [
  { kind: 'image', extensions: ['jpg', 'jpeg', 'png', 'gif'], mimes: ['image/jpeg', 'image/png', 'image/gif'], formats: 'JPG, PNG, GIF' },
  { kind: 'video', extensions: ['mp4', 'm4v', 'webm'], mimes: ['video/mp4', 'video/x-m4v', 'video/webm'], formats: 'MP4, WEBM' },
  { kind: 'audio', extensions: ['mp3', 'm4a', 'aac', 'wav', 'ogg'], mimes: ['audio/mpeg', 'audio/mp3', 'audio/mp4', 'audio/x-m4a', 'audio/aac', 'audio/wav', 'audio/x-wav', 'audio/wave', 'audio/ogg'], formats: 'MP3, M4A, AAC, WAV, OGG' },
  {
    kind: 'document',
    extensions: ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'],
    mimes: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ],
    formats: 'PDF, Word, PowerPoint, Excel',
  },
];

/** Android pickers often report no MIME, or a generic one, for a perfectly good file. */
const UNKNOWN_MIMES = ['', 'application/octet-stream'];

export function uploadKindSpec(kind: UploadKind): UploadKindSpec {
  return UPLOAD_KINDS.filter((spec) => spec.kind === kind)[0];
}

export function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/** The `accept` attribute for one tab's file picker. */
export function uploadAccept(kind: UploadKind): string {
  const spec = uploadKindSpec(kind);
  return spec.extensions.map((extension) => `.${extension}`).concat(spec.mimes).join(',');
}

/** Images keep the original strict check: extension and MIME must both be known. */
export function isSupportedImage(file: UploadCandidate): boolean {
  const spec = uploadKindSpec('image');
  return file.size <= MAX_IMAGE_UPLOAD_SIZE && spec.extensions.indexOf(fileExtension(file.name)) >= 0 && spec.mimes.indexOf(file.type.toLowerCase()) >= 0;
}

export function isSupportedUpload(file: UploadCandidate, kind: UploadKind): boolean {
  if (kind === 'image') return isSupportedImage(file);
  const spec = uploadKindSpec(kind);
  const mime = file.type.toLowerCase();
  const mimeOk = spec.mimes.indexOf(mime) >= 0 || UNKNOWN_MIMES.indexOf(mime) >= 0;
  return file.size <= MAX_UPLOAD_SIZE && spec.extensions.indexOf(fileExtension(file.name)) >= 0 && mimeOk;
}

/** Why a picked file was refused, in the tab's own words. */
export function uploadRejection(file: UploadCandidate, kind: UploadKind): string {
  if (file.size > MAX_UPLOAD_SIZE) return `Tệp "${file.name}" vượt quá ${MAX_UPLOAD_SIZE_LABEL}. Máy chủ chỉ nhận tệp tối đa ${MAX_UPLOAD_SIZE_LABEL}.`;
  return `Tệp "${file.name}" không đúng định dạng. Mục này nhận ${uploadKindSpec(kind).formats}.`;
}

/** The tab a stored upload belongs to, from what the server echoed back; null for anything else. */
export function classifyUpload(file: { name: string; mime?: string | null; ext?: string | null }): UploadKind | null {
  const mime = (file.mime || '').toLowerCase();
  const extension = (file.ext || '').replace(/^\./, '').toLowerCase() || fileExtension(file.name);
  for (let i = 0; i < UPLOAD_KINDS.length; i += 1) {
    const spec = UPLOAD_KINDS[i];
    if (spec.mimes.indexOf(mime) >= 0) return spec.kind;
  }
  for (let i = 0; i < UPLOAD_KINDS.length; i += 1) {
    const spec = UPLOAD_KINDS[i];
    if (spec.extensions.indexOf(extension) >= 0) return spec.kind;
  }
  return null;
}

export function uploadViewer(file: { name: string; mime?: string | null; ext?: string | null }): UploadViewer {
  const kind = classifyUpload(file);
  if (kind === 'image' || kind === 'video' || kind === 'audio') return kind;
  const pdf = (file.mime || '').toLowerCase() === 'application/pdf' || fileExtension(file.name) === 'pdf' || (file.ext || '').toLowerCase() === '.pdf';
  return pdf ? 'pdf' : 'file';
}
